import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/design-system';

export interface FormErrorSummaryProps {
  title?: ReactNode;
  /** Field errors as { id, message }; each becomes a link that focuses the field. */
  fieldErrors?: { name: string; message: string }[];
  messages?: string[];
  children?: ReactNode;
}

/** Shown at the top of a form after a failed submit. Focused so screen readers announce it. */
export function FormErrorSummary({ title, fieldErrors = [], messages = [], children }: FormErrorSummaryProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const count = fieldErrors.length + messages.length;
  useEffect(() => {
    ref.current?.focus();
  }, [title, count]);
  if (!count && !title && !children) return null;
  return (
    <div ref={ref} tabIndex={-1} className="outline-none">
      <Alert tone="negative" title={title ?? t('form.errorSummary', { count })}>
        {count ? (
          <ul className="mt-1 list-disc ps-4">
            {fieldErrors.map((e) => (
              <li key={e.name}>
                <button
                  type="button"
                  className="text-start underline underline-offset-2 hover:text-ink"
                  onClick={() => {
                    const el = document.querySelector<HTMLElement>(`[name="${CSS.escape(e.name)}"]`);
                    el?.focus();
                    el?.scrollIntoView({ block: 'center' });
                  }}
                >
                  {t(e.message as 'form.required', { defaultValue: e.message })}
                </button>
              </li>
            ))}
            {messages.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        ) : null}
        {children}
      </Alert>
    </div>
  );
}
